/**
 * Maps technical error responses to user-friendly messages.
 * Never exposes stack traces, exception names, or technical jargon to users.
 */
export function getFriendlyErrorMessage(err, context = '') {
  const status = err.response?.status;
  const serverMsg = err.response?.data?.title || err.response?.data?.message || '';

  // Don't show technical error messages to users
  const isTechnical = /exception|error|stack|trace|dbupdate|internal|failed/i.test(serverMsg);

  if (status === 401) {
    return 'Your session has expired. Please log in again.';
  }

  if (status === 403) {
    return "You don't have permission to perform this action.";
  }

  if (status === 404) {
    // Check for specific 404 contexts
    if (/owner not found|reset.*password|owner account|hasn't been created/i.test(serverMsg)) {
      return 'This tenant\'s owner account hasn\'t been created yet. Please provision the tenant first by going to the Environments tab and clicking "Provision tenant".';
    }
    if (context === 'tenant') return 'This tenant could not be found.';
    if (context === 'feature') return 'This feature could not be found.';
    if (context === 'plan') return 'This subscription plan could not be found.';
    return 'The requested resource was not found.';
  }

  if (status === 409) {
    // Conflict - usually duplicate data
    if (/already exists/i.test(serverMsg) || /duplicate/i.test(serverMsg)) {
      if (context === 'tenant') return 'A tenant with this name already exists. Please choose a different name.';
      return 'This item already exists. Please choose a different name.';
    }
    // Surface an actionable, non-technical server message (e.g. the "provision an environment
    // first" guard when activating an unprovisioned tenant) rather than a misleading "refresh".
    if (serverMsg && !isTechnical) return serverMsg;
    return 'This action conflicts with existing data. Please refresh and try again.';
  }

  if (status === 422) {
    return 'Some of the information provided is invalid. Please check your entries and try again.';
  }

  if (status === 500) {
    return 'Something went wrong on our end. Please try again. If the problem persists, contact support.';
  }

  if (status === 502) {
    return 'The server is temporarily unavailable. Please try again in a moment.';
  }

  if (!err.response) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }

  // Use server message only if it's user-friendly
  if (serverMsg && !isTechnical) {
    return serverMsg;
  }

  return 'An unexpected error occurred. Please try again.';
}

// Field names whose values must never be rendered — obvious secret-looking keys.
const SECRET_KEY_RE = /token|secret|password|passwd|authorization|api[-_]?key|bearer|credential/i;

/**
 * Extracts the RAW, un-sanitized server error detail for OPERATOR/staff eyes
 * only. Unlike getFriendlyErrorMessage (which is customer-safe), this preserves
 * the real server cause so staff on the SuperAdmin console can debug.
 *
 * Never renders secret-looking object fields, redacts JWT/Bearer tokens found in
 * free text, and truncates so a stack trace or huge blob is never dumped.
 *
 * @returns {{ status: number|null, message: string|null } | null}
 */
export function getRawErrorDetail(err) {
  const status = err?.response?.status ?? null;
  const data = err?.response?.data;
  let raw = '';

  if (typeof data === 'string') {
    raw = data;
  } else if (data && typeof data === 'object') {
    // Prefer the descriptive fields the backend uses (ProblemDetails etc.).
    raw = data.detail || data.message || data.title || data.error || '';
    // Fall back to a compact dump of non-secret, non-nested fields.
    if (!raw) {
      const safe = {};
      for (const [k, v] of Object.entries(data)) {
        if (SECRET_KEY_RE.test(k)) continue;
        if (v && typeof v === 'object') continue; // skip nested blobs
        safe[k] = v;
      }
      if (Object.keys(safe).length) raw = JSON.stringify(safe);
    }
  }

  raw = String(raw || '')
    .replace(/\s+/g, ' ')
    .trim()
    // Redact anything that itself looks like a token/JWT.
    .replace(/\beyJ[a-zA-Z0-9._-]{10,}/g, '[redacted]')
    .replace(/\bBearer\s+[A-Za-z0-9._-]+/gi, 'Bearer [redacted]');

  // Truncate — operators get the gist, not a stack trace.
  const MAX = 300;
  if (raw.length > MAX) raw = `${raw.slice(0, MAX)}…`;

  if (status === null && !raw) return null;
  return { status, message: raw || null };
}
